import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, CircularProgress, Alert, Grid } from "@mui/material";
import TaskScheduleCard from "../TaskScheduleCard";
import {
  getExternalTasks,
  configureExternalTask,
  getCorrelationsTasks,
  configureCorrelationsTask,
} from "../../services/settings_services";
import { useModules } from "../../ModuleContext";

const THRESHOLD_FIELD = [
  {
    key: "threshold",
    label: "Threshold",
    tooltip: "Minimum similarity threshold for correlation matching",
    maxWidth: 120,
    inputProps: { min: 0, max: 1, step: 0.01 },
  },
];

const SchedulingTab = () => {
  const { modules } = useModules();
  const [externalTasks, setExternalTasks] = useState([]);
  const [correlationsTasks, setCorrelationsTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const [ext, corr] = await Promise.all([
        modules.cti_gathering ? getExternalTasks() : [],
        modules.correlations ? getCorrelationsTasks() : [],
      ]);
      if (signal?.aborted) return;
      setExternalTasks(ext);
      setCorrelationsTasks(Array.isArray(corr) ? corr : [corr]);
    } catch {
      if (signal?.aborted) return;
      setError("Failed to load task schedules");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [modules.cti_gathering, modules.correlations]);

  useEffect(() => {
    if (!modules.cti_gathering && !modules.correlations) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    fetchAll(ac.signal);
    return () => ac.abort();
  }, [fetchAll]);

  const handleExternalSave = (task) => async (values) => {
    await configureExternalTask({
      task_name: task.task_name,
      schedule: { hour: values.hour, minute: values.minute },
      enabled: values.enabled,
    });
    fetchAll();
  };

  const handleCorrelationsSave = (task) => async (values) => {
    await configureCorrelationsTask({
      task_name: task.task_name,
      schedule: { hour: values.hour, minute: values.minute },
      enabled: values.enabled,
      threshold: values.threshold,
    });
    fetchAll();
  };

  if (!modules.cti_gathering && !modules.correlations) {
    return (
      <Alert severity="info">
        Enable the CTI Gathering or Correlations module to configure task schedules.
      </Alert>
    );
  }

  if (loading) return <CircularProgress sx={{ m: 2 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {modules.cti_gathering && externalTasks.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            CTI Gathering — External Sources
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {externalTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.task_name}>
                <TaskScheduleCard task={task} onSave={handleExternalSave(task)} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {modules.correlations && correlationsTasks.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            Correlations
          </Typography>
          <Grid container spacing={2}>
            {correlationsTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.task_name}>
                <TaskScheduleCard
                  task={task}
                  onSave={handleCorrelationsSave(task)}
                  extraFields={THRESHOLD_FIELD}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default SchedulingTab;
